// auto_zoom, popup_mobile_breakpoint, log_event, normalize_site_id and visible_bounds are referenced by filters.js
// and site-listing.js (all concatenated into main.js), so they must stay at the
// top level rather than move inside init_map() below.
const auto_zoom = 10; // Zoom level to fly to when a site is selected or a zip code is searched
const cluster_max_zoom = 14; // Highest zoom at which markers still cluster
const popup_mobile_breakpoint = 600; // map width at which to switch from desktop to mobile popups; should match the mobile breakpoint in _layout.scss

function log_event(name, data) {
    let log_data = {};
    switch(name) {
        case 'marker_hover':
        case 'marker_click':
            log_data = {
                record_id: data.record_id,
                name: data.name,
                state: data.state,
                city: data.city,
                type: data.pin_code
            };
            break;
        case 'site_card_click':
        case 'phone_click':
            const site_card = document.querySelector('[data-record-id="' + data.recordId + '"]');
            log_data = {
                record_id: data.recordId,
                state: data.state,
                city: data.city,
                type: data.pinCode,
                name: site_card ? site_card.querySelector('.pcsl-site-card-name').textContent : undefined
            };
            break;
        default:
            log_data = data;
            break;
    }
    window.dispatchEvent(new CustomEvent('pcsl-event', { detail: { name: name, data: log_data } }));
}

// Canonical form of a FileMaker site ID ("ca09249" -> "CA9249"): state letters
// uppercased, leading zeros dropped, so ?site= matches however the ID is typed.
// Returns '' for anything else. Must match pcsl_normalize_site_id() in index.php.
function normalize_site_id(site_id) {
    const m = String(site_id || '').trim().match(/^([A-Za-z]{2})0*(\d+)$/);
    return m ? m[1].toUpperCase() + m[2] : '';
}

// The geographic area the whole map canvas shows. map.getBounds() leaves out the
// camera padding, and fitBounds({ padding }) keeps that padding on the map
// afterwards, so getBounds() would miss sites drawn near the edges.
function visible_bounds(map) {
    const canvas = map.getCanvas();
    return new mapboxgl.LngLatBounds(
        map.unproject([0, canvas.clientHeight]),
        map.unproject([canvas.clientWidth, 0])
    );
}

// Creating the Mapbox map (new mapboxgl.Map below) triggers a billable Mapbox
// "map load": it fetches the Studio style, tiles and fonts over the network. To
// keep bot traffic and visitors who never reach the block from running up the
// bill, this whole setup is deferred until the site locator scrolls into view --
// see the IntersectionObserver at the bottom of the file. The function body is
// intentionally left at its original indentation to keep the diff reviewable.
function init_map() {
mapboxgl.accessToken = pcsl_data.mapbox_token;

const map_container = document.getElementById('pcsl-map');
// URL parameters (?site=, ?state=, ?type=) take precedence over the shortcode
// attributes, so ads and emails can link straight to a site or a filtered view.
const url_params = new URLSearchParams(window.location.search);
const deep_link_site_id = (url_params.get('site') || '').trim();
const initial_state = (url_params.get('state') || map_container.getAttribute('data-initial-state') || '').trim();
const initial_type = (url_params.get('type') || map_container.getAttribute('data-initial-type') || '').trim();

function is_desktop() {
    return map_container.closest('.pcsl-site-locator-inner').offsetWidth > popup_mobile_breakpoint;
}

const map = new mapboxgl.Map({
    container: 'pcsl-map',
    style: 'mapbox://styles/paintcare/cmpczn16o007w01s30rekfhm4',
    center: [-98.5, 39.5],
    zoom: 4
});
map.addControl(new mapboxgl.NavigationControl({
    showCompass: false
}));

const mobile_legend = new MobileLegend();
const list_toggle = new ListToggle();

// Track user-initiated map movement so we can tell the difference between the
// initial programmatic fitBounds and any subsequent panning/zooming by the user.
// Mapbox only attaches originalEvent to events triggered by user interaction.
let user_moved_map = false;
const mark_user_moved = (e) => { if (e.originalEvent) user_moved_map = true; };
map.on('dragstart', mark_user_moved);
map.on('zoomstart', mark_user_moved);
map.on('rotatestart', mark_user_moved);
map.on('pitchstart', mark_user_moved);

// Holds the initial_state bbox so other components (e.g. the full-width toggle)
// can refit the viewport to it without re-reading data-initial-state.
let initial_state_bbox = null;

const full_width_toggle = new FullWidthToggle(map, {
    on_first_enter: () => {
        if (!user_moved_map && initial_state_bbox) {
            map.fitBounds(initial_state_bbox);
        }
    }
});

// --- Filter state ---
let all_features = [];
let map_loaded = false;

// Kept as a promise so the ?site= deep link can wait for the site data as well
// as the map's load event, whichever finishes last.
const features_ready = fetch(pcsl_data.geojson_url)
    .then(r => r.json())
    .then(data => {
        all_features = data.features || [];
        filters.apply();
    });

// --- Filters ---
const filters = new Filters(map, () => all_features, () => map_loaded, () => site_listing);

// --- Site list ---
const site_listing = new SiteListing(map, () => filters.get_filtered_features());

// --- Map setup ---
function load_map_image(url, name) {
    if (!url.toLowerCase().endsWith('.svg')) {
        map.loadImage(url, (error, image) => {
            if (error) throw error;
            map.addImage(name, image);
        });
        return;
    }

    fetch(url)
        .then(r => r.text())
        .then(svg_text => {
            const parser = new DOMParser();
            const svg_el = parser.parseFromString(svg_text, 'image/svg+xml').querySelector('svg');

            let w = parseFloat(svg_el.getAttribute('width')) || 0;
            let h = parseFloat(svg_el.getAttribute('height')) || 0;

            if (!w || !h) {
                const vb = svg_el.getAttribute('viewBox');
                if (vb) {
                    const parts = vb.trim().split(/[\s,]+/);
                    w = w || parseFloat(parts[2]);
                    h = h || parseFloat(parts[3]);
                }
            }

            w = w || 40;
            h = h || 40;

            const blob_url = URL.createObjectURL(new Blob([svg_text], { type: 'image/svg+xml' }));
            const img = new Image(w, h);
            img.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width = w;
                canvas.height = h;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, w, h);
                map.addImage(name, ctx.getImageData(0, 0, w, h));
                URL.revokeObjectURL(blob_url);
            };
            img.onerror = () => URL.revokeObjectURL(blob_url);
            img.src = blob_url;
        });
}

map.on('load', () => {
    // these are coming from the mapbox studio style now
    //load_map_image(pcsl_data.site_marker_file_url, 'pcsl-site-marker');
    //load_map_image(pcsl_data.selected_site_marker_file_url, 'pcsl-site-selected-marker');
    //load_map_image(pcsl_data.non_site_marker_file_url, 'pcsl-non-site-marker');
    //load_map_image(pcsl_data.selected_non_site_marker_file_url, 'pcsl-non-site-selected-marker');

    const paintcare_states = pcsl_data.paintcare_states || [];

    map.addSource('pcsl-sites', {
        type: 'geojson',
        data: pcsl_data.geojson_url,
        cluster: true,
        clusterMaxZoom: cluster_max_zoom,
        clusterProperties: {
            non_pc_count: [
                ['+', ['accumulated'], ['get', 'non_pc_count']],
                ['match', ['get', 'state'], paintcare_states, 0, 1]
            ]
        }
    });

    map.addLayer({
        id: 'pcsl-clusters',
        type: 'circle',
        source: 'pcsl-sites',
        filter: ['has', 'point_count'],
        paint: {
            'circle-color': [
                'case',
                ['>', ['/', ['get', 'non_pc_count'], ['get', 'point_count']], 0.9],
                '#8a8a8a',
                '#134298'
            ],
            'circle-radius': 19
        }
    });

    map.addLayer({
        id: 'pcsl-cluster-count',
        type: 'symbol',
        source: 'pcsl-sites',
        filter: ['has', 'point_count'],
        layout: {
            'text-field': '{point_count_abbreviated}',
            'text-size': 12,
            'text-offset': [0, 0.2],
            'text-font': ['Roboto Condensed', 'Arial Unicode MS Bold'],
            'text-anchor': 'center',
            'text-justify': 'center'
        },
        paint: {
            'text-color': '#ffffff'
        }
    });

    const marker_image_expr = paintcare_states.length
        ? ['match', ['get', 'state'], paintcare_states, 'pcsl-site-marker', 'pcsl-non-site-marker']
        : 'pcsl-non-site-marker';
    const hover_image_expr = paintcare_states.length
        ? ['match', ['get', 'state'], paintcare_states, 'pcsl-site-hover-marker-desktop', 'pcsl-non-site-hover-marker-desktop']
        : 'pcsl-non-site-hover-marker-desktop';

    function get_selected_image_expr() {
        const suffix = is_desktop() ? 'desktop' : 'mobile';
        return paintcare_states.length
            ? ['match', ['get', 'state'], paintcare_states, `pcsl-site-selected-marker-${suffix}`, `pcsl-non-site-selected-marker-${suffix}`]
            : `pcsl-non-site-selected-marker-${suffix}`;
    }

    let selected_record_id = null;

    function set_selected(record_id) {
        selected_record_id = record_id;
        map.setLayoutProperty('pcsl-unclustered-point-selected', 'icon-image', get_selected_image_expr());
        map.setFilter('pcsl-unclustered-point-selected', ['==', 'record_id', record_id]);
    }

    function clear_selected() {
        selected_record_id = null;
        map.setFilter('pcsl-unclustered-point-selected', ['==', 'record_id', '']);
    }

    map.addLayer({
        id: 'pcsl-unclustered-point',
        type: 'symbol',
        source: 'pcsl-sites',
        filter: ['!', ['has', 'point_count']],
        layout: {
            'icon-image': marker_image_expr,
            'icon-anchor': 'bottom',
            'icon-allow-overlap': true,
            'icon-size': 1
        }
    });

    map.addLayer({
        id: 'pcsl-unclustered-point-selected',
        type: 'symbol',
        source: 'pcsl-sites',
        filter: ['==', 'record_id', ''],
        layout: {
            'icon-image': get_selected_image_expr(),
            'icon-anchor': 'bottom',
            'icon-allow-overlap': true,
            'icon-size': 1
        }
    });

    map.addLayer({
        id: 'pcsl-unclustered-point-hover',
        type: 'symbol',
        source: 'pcsl-sites',
        filter: ['==', 'record_id', ''],
        layout: {
            'icon-image': hover_image_expr,
            'icon-anchor': 'bottom',
            'icon-allow-overlap': true,
            'icon-size': 1
        }
    });

    map.on('click', (e) => {
        if (!e.features || !e.features.length) {
            document.querySelector('.pcsl-filters-inner').classList.remove('open');
        }
        if (site_listing.pinned_record_id) {
            const rendered = map.queryRenderedFeatures({ layers: ['pcsl-unclustered-point'] });
            const still_visible = rendered.some(f => f.properties.record_id === site_listing.pinned_record_id);
            if (!still_visible) {
                site_listing.unpin();
                clear_selected();
            }
        }
        hideMobilePopupLightbox();
        mobile_legend.close();
    });

    map.on('click', 'pcsl-clusters', (e) => {
        const feature = e.features[0];
        const cluster_id = feature.properties.cluster_id;
        const coordinates = feature.geometry.coordinates;

        map.getSource('pcsl-sites').getClusterExpansionZoom(cluster_id, (err, zoom) => {
            if (err) return;
            map.easeTo({ center: coordinates, zoom });
        });        
    });

    map.on('mouseenter', 'pcsl-clusters', () => { map.getCanvas().style.cursor = 'pointer'; });
    map.on('mouseleave', 'pcsl-clusters', () => { map.getCanvas().style.cursor = ''; });

    const popup = new mapboxgl.Popup({ closeButton: false, closeOnClick: false, offset: 30, anchor: 'bottom-left' });

    const mobile_popup = document.createElement('div');
    mobile_popup.className = 'pcsl-map-lightbox pcsl-map-lightbox--hidden';
    mobile_popup.innerHTML = `<div class="pcsl-map-lightbox-close" aria-label="Close">
        <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 26 26" fill="none">
        <rect x="7.13754" y="5.57155" width="18.5714" height="1.9898" transform="rotate(45 7.13754 5.57155)" fill="#134298"/>
        <rect x="5.73062" y="18.7035" width="18.5714" height="1.9898" transform="rotate(-45 5.73062 18.7035)" fill="#134298"/>
        </svg>
    </div><div class="pcsl-map-lightbox-content"></div>`;
    map_container.appendChild(mobile_popup);
    mobile_popup.querySelector('.pcsl-map-lightbox-close').addEventListener('click', hideMobilePopupLightbox);

    function hideMobilePopupLightbox() {
        mobile_popup.classList.add('pcsl-map-lightbox--hidden');
    }
    function hidePopup() {
        popup.remove();        
    }
    function showPopup(feature) {
        const coordinates = feature.geometry.coordinates.slice();

        if (map_container.closest('.pcsl-site-locator-inner').offsetWidth <= popup_mobile_breakpoint) {
            mobile_popup.querySelector('.pcsl-map-lightbox-content').innerHTML = site_listing.buildSiteCard(feature.properties, coordinates);
            mobile_popup.classList.remove('pcsl-map-lightbox--hidden');            
        } else {
            const { name, comment3, pin_code, state } = feature.properties;
            const is_paintcare_state = paintcare_states.includes(state);
            let non_paintcare_html = '';
            if (!is_paintcare_state) {
                non_paintcare_html = `<div class='pcsl-site-card-non-paintcare-state pcsl-site-card-chip'>${state} is not a PaintCare State</div>`;
            }
            let paint_reuse_html = '';
            if (pin_code.includes('Reuse')) {
                paint_reuse_html = `<div class='pcsl-popup-paint-reuse pcsl-site-card-chip'>Paint reuse available</div>`;
            }
            let comment3_html = '';
            if (comment3 && comment3.length < 30) {
                comment3_html = `<span class='pcsl-popup-comment pcsl-site-card-chip'>${comment3}</span>`;
            }
            popup.setLngLat(coordinates).setHTML(`${non_paintcare_html}<div class='pcsl-popup-title'>${name}</div>${paint_reuse_html}${comment3_html}`).addTo(map);
        }
    }

    function on_marker_mouseenter(e) {
        map.getCanvas().style.cursor = 'pointer';
        const feature = e.features[0];
        const { record_id } = feature.properties;
        map.setFilter('pcsl-unclustered-point-hover', ['==', 'record_id', record_id]);
        showPopup(feature);
        log_event('marker_hover', feature.properties);
    }

    function on_marker_mouseleave() {
        map.getCanvas().style.cursor = '';
        map.setFilter('pcsl-unclustered-point-hover', ['==', 'record_id', '']);
        hidePopup();
    }

    function initHoverListeners() {
        map.off('mouseenter', 'pcsl-unclustered-point', on_marker_mouseenter);
        map.off('mouseleave', 'pcsl-unclustered-point', on_marker_mouseleave);
        hidePopup();
        if (is_desktop()) {
            map.on('mouseenter', 'pcsl-unclustered-point', on_marker_mouseenter);
            map.on('mouseleave', 'pcsl-unclustered-point', on_marker_mouseleave);            
        }
    }

    // Shared by marker clicks and the ?site= deep link. The deep link jumps
    // (duration 0) rather than flies: the move is then synchronous, so the
    // site is already inside the map bounds when site_listing.pin() checks them.
    function select_site(feature, { animate = true, zoom = auto_zoom } = {}) {
        const { record_id } = feature.properties;
        const coordinates = feature.geometry.coordinates.slice();
        const fly_options = { center: coordinates, zoom: Math.max(map.getZoom(), zoom) };
        if (!is_desktop()) fly_options.offset = [0, 170 - map_container.offsetHeight / 2];
        if (animate) map.flyTo(fly_options); else map.easeTo({ ...fly_options, duration: 0 });
        site_listing.pin(record_id);
        set_selected(record_id);
        showPopup(feature);
        mobile_legend.close();
    }

    map.on('click', 'pcsl-unclustered-point', (e) => {
        const feature = e.features[0];
        select_site(feature);
        log_event('marker_click', feature.properties);
    });

    // ?site= takes either a record ID (all digits, e.g. 5143) or PaintCare's site
    // ID (state + number, e.g. CA09249). Site IDs repeat across states without
    // the state prefix, and many sites have none, so record IDs keep working.
    function find_deep_link_site() {
        if (/^\d+$/.test(deep_link_site_id)) {
            return all_features.find(f => f.properties.record_id === deep_link_site_id);
        }
        const site_id = normalize_site_id(deep_link_site_id);
        return site_id ? all_features.find(f => normalize_site_id(f.properties.site_id) === site_id) : undefined;
    }

    function open_deep_link() {
        const feature = find_deep_link_site();
        if (!feature) {
            // Unknown or since-removed site: carry on as an ordinary page load.
            log_event('deep_link', { link_id: deep_link_site_id, found: false });
            if (!initial_state_bbox) locate_visitor();
            return;
        }

        // A type or state filter (from ?type=/?state= or the shortcode) must not
        // hide the linked site.
        const type_select = document.getElementById('pcsl-pin-code-select');
        if (type_select.value && !(feature.properties.pin_code || '').includes(type_select.value)) {
            type_select.value = '';
            filters.apply({ reveal: false });
            filters.update_search_term();
        }
        if (filters.selected_state && filters.selected_state !== feature.properties.state) {
            filters.set_state('');
            filters.apply({ reveal: false });
        }

        // The site now stands in for any state view; don't refit to the state later.
        initial_state_bbox = null;
        select_site(feature, { animate: false });

        // At auto_zoom the site can still be inside a cluster, which hides its
        // selected marker. Once tiles render, zoom past cluster_max_zoom if needed,
        // unless the visitor has already moved on to another site.
        const { record_id, site_id, name, state, city, pin_code } = feature.properties;
        map.once('idle', () => {
            if (selected_record_id !== record_id) return;
            const rendered = map.queryRenderedFeatures({ layers: ['pcsl-unclustered-point'] })
                .some(f => f.properties.record_id === record_id);
            if (!rendered) select_site(feature, { zoom: cluster_max_zoom + 1 });
        });

        log_event('deep_link', { record_id, site_id, link_id: deep_link_site_id, found: true, name, state, city, type: pin_code });
    }

    const list_el = document.getElementById('pcsl-site-list');
    let hovered_card = null;

    list_el.addEventListener('click', e => {
        const card = e.target.closest('.pcsl-site-card');
        if (!card) return;
        set_selected(card.dataset.recordId);
    });

    list_el.addEventListener('mouseover', e => {
        const card = e.target.closest('.pcsl-site-card');
        if (card === hovered_card) return;
        if (hovered_card) {
            map.setFilter('pcsl-unclustered-point-hover', ['==', 'record_id', '']);
            hidePopup();
        }
        hovered_card = card;
        if (!card) return;
        const feature = all_features.find(f => f.properties.record_id === card.dataset.recordId);
        if (!feature) return;
        if (is_desktop()) {
            map.setFilter('pcsl-unclustered-point-hover', ['==', 'record_id', card.dataset.recordId]);
        }
        showPopup(feature);
    });

    list_el.addEventListener('mouseleave', () => {
        if (!hovered_card) return;
        hovered_card = null;
        map.setFilter('pcsl-unclustered-point-hover', ['==', 'record_id', '']);
        hidePopup();
    });

    window.addEventListener('resize', () => {
        map.resize();
        if (selected_record_id) {
            map.setLayoutProperty('pcsl-unclustered-point-selected', 'icon-image', get_selected_image_expr());
        }
        initHoverListeners();
    });

    // Used when no state was pre-set: try to auto-center on the visitor's location.
    // getCurrentPosition is async and prompts for permission; if the visitor
    // declines, the lookup errors, or geolocation is unavailable (e.g. a
    // non-secure context), we silently keep the default continental view.
    // We also bail if the visitor has already started interacting with the
    // map by the time the lookup resolves, so we never yank the viewport out
    // from under them.
    function locate_visitor() {
        if (!navigator.geolocation) return;
        navigator.geolocation.getCurrentPosition(
            (position) => {
                if (user_moved_map) return;
                const { longitude, latitude } = position.coords;
                map.flyTo({ center: [longitude, latitude], zoom: 9 });
                log_event('geolocation', { status: 'success' });
            },
            (error) => {
                log_event('geolocation', { status: 'error', code: error.code });
            },
            { enableHighAccuracy: false, timeout: 8000, maximumAge: 600000 }
        );
    }

    if (initial_state) {
        const state_bounds = {
            AL: [-88.473, 30.144, -84.888, 35.008], AK: [-179.151, 51.210, -129.980, 71.441],
            AZ: [-114.818, 31.332, -109.045, 37.004], AR: [-94.618, 33.004, -89.644, 36.500],
            CA: [-124.410, 32.534, -114.131, 42.010], CO: [-109.060, 36.992, -102.042, 41.003],
            CT: [-73.728, 40.980, -71.787, 42.037], DE: [-75.789, 38.451, -74.985, 39.839],
            FL: [-87.635, 24.523, -79.974, 31.001], GA: [-85.605, 30.356, -80.840, 35.001],
            HI: [-160.247, 18.911, -154.807, 22.236], ID: [-117.243, 41.988, -111.044, 49.001],
            IL: [-91.513, 36.970, -87.020, 42.508], IN: [-88.100, 37.772, -84.785, 41.761],
            IA: [-96.640, 40.376, -90.140, 43.501], KS: [-102.052, 36.993, -94.588, 40.003],
            KY: [-89.572, 36.497, -81.965, 39.147], LA: [-94.043, 28.929, -88.817, 33.019],
            ME: [-71.084, 42.977, -66.950, 47.453], MD: [-79.488, 37.887, -74.986, 39.723],
            MA: [-73.508, 41.237, -69.928, 42.887], MI: [-90.419, 41.696, -82.414, 48.306],
            MN: [-97.239, 43.499, -89.492, 49.385], MS: [-91.655, 30.148, -88.098, 35.008],
            MO: [-95.775, 35.996, -89.099, 40.614], MT: [-116.049, 44.358, -104.040, 49.001],
            NE: [-104.053, 40.000, -95.308, 43.002], NV: [-120.006, 35.002, -114.040, 42.002],
            NH: [-72.557, 42.697, -70.510, 45.305], NJ: [-75.559, 38.929, -73.894, 41.357],
            NM: [-109.050, 31.332, -103.001, 37.000], NY: [-79.762, 40.496, -71.856, 45.015],
            NC: [-84.322, 33.842, -75.460, 36.588], ND: [-104.049, 45.935, -96.554, 49.001],
            OH: [-84.820, 38.403, -80.519, 41.978], OK: [-103.003, 33.616, -94.431, 37.002],
            OR: [-124.566, 41.992, -116.464, 46.238], PA: [-80.520, 39.720, -74.690, 42.270],
            RI: [-71.863, 41.146, -71.120, 42.019], SC: [-83.353, 32.035, -78.542, 35.216],
            SD: [-104.058, 42.480, -96.436, 45.945], TN: [-90.310, 34.983, -81.647, 36.678],
            TX: [-106.646, 25.837, -93.508, 36.501], UT: [-114.053, 36.998, -109.042, 42.002],
            VT: [-73.438, 42.727, -71.465, 45.015], VA: [-83.675, 36.541, -75.242, 39.466],
            WA: [-124.733, 45.544, -116.916, 49.002], WV: [-82.645, 37.201, -77.720, 40.639],
            WI: [-92.889, 42.492, -86.250, 47.081], WY: [-111.055, 40.995, -104.052, 45.006],
            DC: [-77.120, 38.792, -76.909, 38.995],
        };
        // Hand-tuned view bboxes for states whose true bounds produce an
        // off-center initial view. Maryland's western panhandle drags the
        // fitted view west of the state's visual mass, so we fit a bbox
        // symmetric around the state's centroid instead, accepting that
        // the panhandle may start outside the viewport.
        const state_view_overrides = {
            MD: [-78.41, 37.887, -74.986, 39.723],
        };
        const state_key = initial_state.toUpperCase();
        const bbox = state_view_overrides[state_key] || state_bounds[state_key];
        if (bbox) {
            initial_state_bbox = bbox;
            map.fitBounds(bbox);
            // State pages list only that state's sites, as a state search does.
            filters.set_state(state_key, site_listing.getStateName(state_key));
        }
        log_event('map_load', { state: initial_state });
    } else {
        log_event('map_load', { state: '' });
        // A ?site= deep link positions the map itself; open_deep_link() falls
        // back to locate_visitor() when the site isn't found.
        if (!deep_link_site_id) locate_visitor();
    }

    if (initial_type) {
        const type_select = document.getElementById('pcsl-pin-code-select');
        // Case-insensitive so hand-typed links like ?type=hhw still match.
        const type_option = type_select && [...type_select.options].find(o => o.value && o.value.toLowerCase() === initial_type.toLowerCase());
        if (type_option) {
            type_select.value = type_option.value;
            filters.update_search_term();
        }
    }

    map_loaded = true;
    initHoverListeners();
    filters.apply();

    if (deep_link_site_id) features_ready.then(open_deep_link);
});
}

// Defer the Mapbox map load until the site locator block is (nearly) scrolled
// into view. The rootMargin gives the map a head start so it's ready by the time
// the block is fully visible. init_map() runs at most once, then we stop
// observing. If the block is already in view on load (above the fold) the
// observer fires immediately, matching the previous behavior for those visitors.
const pcsl_locator_el = document.getElementById('pcsl-site-locator');
if (pcsl_locator_el) {
    // A link carrying locator parameters (e.g. an ad pointing at one site) should
    // land on the locator rather than the top of the page. Bringing it into view
    // also triggers the lazy map load below. Waits for window load because images
    // above the locator move it down as they arrive, and leaves the page alone if
    // the visitor has already started scrolling.
    const pcsl_url_params = new URLSearchParams(window.location.search);
    if (['site', 'state', 'type'].some(k => pcsl_url_params.has(k))) {
        const scroll_to_locator = () => {
            if (window.scrollY > 0) return;
            if (pcsl_locator_el.getBoundingClientRect().top > window.innerHeight / 3) {
                pcsl_locator_el.scrollIntoView({ block: 'start' });
            }
        };
        if (document.readyState === 'complete') scroll_to_locator();
        else window.addEventListener('load', scroll_to_locator, { once: true });
    }

    if ('IntersectionObserver' in window) {
        const pcsl_map_observer = new IntersectionObserver((entries, observer) => {
            if (entries.some(entry => entry.isIntersecting)) {
                observer.disconnect();
                init_map();
            }
        }, { rootMargin: '100px' });
        pcsl_map_observer.observe(pcsl_locator_el);
    } else {
        init_map();
    }
}
